import { useGetSiteReaderDevicesQuery } from 'apollo/generated/client-operations';
import React from 'react';
import { type SiteReader, siteReadersOf } from './siteReaders';

/**
 * The readers open on the site, as the layout designer offers them for
 * placing. Undefined until loaded, so the designer can tell "unknown" from
 * "none"; `failed` says the read failed rather than is still running.
 */
export const useSiteReaders = (
  siteId: string,
): {
  readers: SiteReader[] | undefined;
  failed: boolean;
  refetch: () => Promise<void>;
} => {
  const { data, error, refetch } = useGetSiteReaderDevicesQuery({
    variables: { jobsiteId: siteId },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });
  const readers = React.useMemo(
    () => (data ? siteReadersOf(data) : undefined),
    [data],
  );
  // A failed read again is shown through `failed`; it never rejects.
  const refetchReaders = React.useCallback(async (): Promise<void> => {
    await refetch().catch((): undefined => undefined);
  }, [refetch]);
  return { readers, failed: !data && Boolean(error), refetch: refetchReaders };
};
